import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useCallback } from 'react'

/**
 * Navigation within the teller area, before the route tree knows about it.
 *
 * TanStack Router types `to`, `params`, and `search` against the registered route tree in
 * `src/router.tsx`, and these routes are registered by the integrator after this area is built.
 * Until then a literal `/teller/sessions` is not assignable to the union of known paths and the
 * whole area fails to compile — so the paths travel as strings through the two loosened helpers
 * below, and the strings are the ones in `tellerRoutes`.
 *
 * This is a temporary seam, not a pattern to copy. Once the routes are wired, `TELLER_PATHS`
 * becomes the same set of literals the router itself declares, and these helpers can be replaced by
 * the typed hooks with no change to any screen: every call site already passes a real path.
 */

export const TELLER_PATHS = {
  counter: '/teller',
  cashPosition: '/teller/cash',
  sessions: '/teller/sessions',
  session: (sessionId: string) => `/teller/sessions/${encodeURIComponent(sessionId)}`,
  till: (tillId: string) => `/teller/tills/${encodeURIComponent(tillId)}`,
  setup: '/teller/setup',
  verificationOverrides: '/teller/verification-overrides',
} as const

export type TellerSearchValues = Record<string, unknown>

interface LooseNavigateOptions {
  to: string
  search?: TellerSearchValues
  replace?: boolean
}

/**
 * `navigate`, addressed by path string.
 *
 * Search values are passed as an object rather than pasted into the path, so the router owns the
 * serialisation and a filtered list stays a shareable link.
 */
export function useTellerNavigate() {
  const navigate = useNavigate()
  const loose = navigate as unknown as (options: LooseNavigateOptions) => Promise<void>

  return useCallback(
    (to: string, options?: { search?: TellerSearchValues; replace?: boolean }) => {
      void loose({ to, ...options })
    },
    [loose],
  )
}

/** Path parameters, read without a route id. Every value is a string or absent. */
export function useTellerParams(): Record<string, string | undefined> {
  const params: Record<string, string | undefined> = useParams({ strict: false })
  return params
}

/**
 * The raw search object.
 *
 * It is read loosely and then parsed by the screen's own zod schema, so a hand-edited URL, a stale
 * link, or a route the integrator has not yet given a `validateSearch` all degrade to the defaults
 * rather than throwing an operator out of a screen mid-shift.
 */
export function useTellerRawSearch(): TellerSearchValues {
  const search: TellerSearchValues = useSearch({ strict: false })
  return search
}
